package com.speedsoftware.nibbler.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.CreateInviteRequest
import com.speedsoftware.nibbler.models.InviteDto
import com.speedsoftware.nibbler.models.UserDto
import com.speedsoftware.nibbler.models.UserRole
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.components.ResponsiveScaffold
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

@Composable
fun AdminDashboardScreen(appState: AppState) {
    var users by remember { mutableStateOf<List<UserDto>>(emptyList()) }
    var invites by remember { mutableStateOf<List<InviteDto>>(emptyList()) }
    var inviteEmail by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun loadData() {
        isLoading = true
        scope.launch {
            appState.apiClient.getUsers().onSuccess { users = it }
            appState.apiClient.getInvites().onSuccess { invites = it }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadData()
    }

    ResponsiveScaffold(
        appState = appState,
        title = "Admin Control Panel"
    ) {
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .background(NibblerColors.DarkBackground)
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(NibblerColors.DarkSurfaceContainer)
                        .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                        .padding(20.dp)
                ) {
                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(10.dp).background(NibblerColors.BauhausRed))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(
                                "Invite a new user",
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                fontSize = 16.sp,
                                color = NibblerColors.DarkOnSurface
                            )
                        }
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            "Issue a secure cryptographically signed invite link for a new collaborator",
                            fontFamily = FontFamily.SansSerif,
                            fontSize = 11.sp,
                            color = NibblerColors.DarkOnSurfaceVariant
                        )

                        Spacer(modifier = Modifier.height(16.dp))

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            OutlinedTextField(
                                value = inviteEmail,
                                onValueChange = { inviteEmail = it },
                                label = { Text("Email address", fontFamily = FontFamily.SansSerif, fontSize = 11.sp) },
                                singleLine = true,
                                shape = RoundedCornerShape(8.dp),
                                modifier = Modifier.weight(1f)
                            )
                            Button(
                                onClick = {
                                    if (inviteEmail.isNotBlank()) {
                                        scope.launch {
                                            val res = appState.apiClient.createInvite(
                                                CreateInviteRequest(email = inviteEmail, role = UserRole.USER)
                                            )
                                            res.onSuccess {
                                                message = "Invite generated for ${it.email}. Token: ${it.token}"
                                                inviteEmail = ""
                                                loadData()
                                            }.onFailure {
                                                message = "Failed to create invite: ${it.message}"
                                            }
                                        }
                                    }
                                },
                                shape = RoundedCornerShape(8.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = NibblerColors.BauhausCoral,
                                    contentColor = Color.White
                                ),
                                modifier = Modifier.height(56.dp)
                            ) {
                                Text("+ GENERATE INVITE", fontFamily = FontFamily.SansSerif, fontWeight = FontWeight.Bold, fontSize = 11.sp)
                            }
                        }

                        if (message != null) {
                            Spacer(modifier = Modifier.height(10.dp))
                            Text(
                                text = message ?: "",
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 11.sp,
                                color = NibblerColors.BauhausYellow
                            )
                        }
                    }
                }
            }

            item {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        "Users",
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = NibblerColors.DarkOnSurface
                    )
                    Text(
                        "${users.size} USERS",
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 11.sp,
                        color = NibblerColors.BauhausBlueLight
                    )
                }
            }

            items(users) { user ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(NibblerColors.DarkSurfaceContainer)
                        .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                        .padding(16.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                            Text(
                                text = user.name,
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                fontSize = 15.sp,
                                color = NibblerColors.DarkOnSurface
                            )
                            Text(
                                text = user.email,
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 11.sp,
                                maxLines = 1,
                                overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                                color = NibblerColors.DarkOnSurfaceVariant
                            )
                        }
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            if (user.twoFactorEnabled) {
                                androidx.compose.material3.OutlinedButton(
                                    onClick = {
                                        scope.launch {
                                            appState.apiClient.adminResetTwoFactor(user.id)
                                                .onSuccess { message = "Two-factor authentication reset for ${user.email}." }
                                                .onFailure { message = it.message ?: "Could not reset two-factor authentication." }
                                            loadData()
                                        }
                                    },
                                    shape = RoundedCornerShape(8.dp),
                                    border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.DarkOutlineThin),
                                    contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 8.dp, vertical = 0.dp),
                                    modifier = Modifier.height(26.dp)
                                ) {
                                    Text("2FA ON - RESET", fontFamily = FontFamily.SansSerif, fontSize = 10.sp, color = NibblerColors.BauhausYellow)
                                }
                            }
                            Box(
                                modifier = Modifier
                                    .background(NibblerColors.DarkSurfaceContainerHighest)
                                    .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                                    .padding(horizontal = 8.dp, vertical = 3.dp)
                            ) {
                                Text(
                                    text = user.role.name,
                                    fontFamily = FontFamily.SansSerif,
                                    maxLines = 1,
                                    softWrap = false,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 10.sp,
                                    color = if (user.role == UserRole.SUPER_ADMIN) NibblerColors.BauhausCoral else NibblerColors.BauhausBlueLight
                                )
                            }
                        }
                    }
                }
            }

            item {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        "Invitations",
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 15.sp,
                        color = NibblerColors.DarkOnSurface
                    )
                    Text(
                        "${invites.count { !it.accepted }} PENDING",
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 11.sp,
                        color = NibblerColors.BauhausYellow
                    )
                }
            }

            items(invites) { invite ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(NibblerColors.DarkSurfaceContainer)
                        .border(1.dp, NibblerColors.DarkOutlineThin, RoundedCornerShape(8.dp))
                        .padding(16.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                            Text(
                                text = invite.email,
                                fontFamily = FontFamily.SansSerif,
                                fontWeight = FontWeight.Bold,
                                fontSize = 14.sp,
                                color = NibblerColors.DarkOnSurface
                            )
                            Text(
                                text = "TOKEN: ${invite.token}",
                                fontFamily = FontFamily.SansSerif,
                                fontSize = 10.sp,
                                maxLines = 1,
                                overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                                color = NibblerColors.DarkOnSurfaceVariant
                            )
                        }
                        Box(
                            modifier = Modifier
                                .background(if (invite.accepted) NibblerColors.BauhausBlue.copy(alpha = 0.2f) else NibblerColors.BauhausYellow.copy(alpha = 0.2f))
                                .border(1.dp, if (invite.accepted) NibblerColors.BauhausBlueLight else NibblerColors.BauhausYellow, RoundedCornerShape(8.dp))
                                .padding(horizontal = 8.dp, vertical = 3.dp)
                        ) {
                            Text(
                                text = if (invite.accepted) "Accepted" else "Pending",
                                fontFamily = FontFamily.SansSerif,
                                maxLines = 1,
                                softWrap = false,
                                fontWeight = FontWeight.Bold,
                                fontSize = 10.sp,
                                color = if (invite.accepted) NibblerColors.BauhausBlueLight else NibblerColors.BauhausYellow
                            )
                        }
                    }
                }
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
fun AdminDashboardScreenPreview() {
    val sampleAppState = AppState()
    AdminDashboardScreen(appState = sampleAppState)
}
